import { Injectable } from "@angular/core";
import { ApiService } from "../global.service";
import { Observable } from "rxjs";
import { HttpBackend, HttpClient, HttpParams } from "@angular/common/http";

import { ItemOrder } from "./interfaces/order.interface";
import { ConfigService } from "../config.service";
import { LocalStorageService } from "../storage.service";
import { BusinessContextService } from "../business-context.service";

@Injectable({
    providedIn: 'root'
})
export class OrderDetailService extends ApiService {
    constructor(
        public override http: HttpClient,
        public override config: ConfigService,
        public override localStorageService: LocalStorageService,
        handler: HttpBackend,
        private readonly bizCtx: BusinessContextService,
    ) {
        super(http, config, localStorageService, handler);
    }

    private getBusiness(): string { return this.bizCtx.getSelected() || this.CUSTOMER; }

    private buildParams(extra?: Record<string, string | undefined | null>): HttpParams {
        let params = new HttpParams();
        if (extra) {
            Object.entries(extra).forEach(([k, v]) => {
                if (v !== undefined && v !== null && v !== '') {
                    params = params.set(k, v);
                }
            });
        }
        return params;
    }

    createOrderDetail(payload: { orderUuid: string; menuProductUuid: string; qty: number; createdAt?: string }): Observable<ItemOrder> {
        // Backend expects: POST /order-detail/{businessUuid} with body { orderUuid, menuProductUuid, qty, createdAt? }
        const business = this.getBusiness();
        return this.create<ItemOrder>(`${this.URL}/order-detail/${business}`, payload);
    }

    updateOrderDetail(orderDetail: Partial<ItemOrder>): Observable<ItemOrder> {
        const business = this.getBusiness();
        return this.patch<ItemOrder>(`${this.URL}/order-detail/${business}`, orderDetail);
    }

    processItems(orderUuid: string, itemUuids: string[], action: string, onlyPending: boolean = true): Observable<any> {
        const business = this.getBusiness();
        // Usar ApiService.create para que incluya 'X-Business-Uuid' de forma consistente
        return this.create(`${this.URL}/order-detail/${business}/process`, {
            orderUuid,
            itemUuids,
            action,
            onlyPending
        });
    }

    // Update product options in batch (single call)
    // Backend will handle creating/updating/removing items as needed
    updateProductOptions(payload: {
        orderUuid: string;
        menuProductUuid: string;
        orderDetailUuid?: string;
        options: Array<{ optionUuid: string; qty: number }>;
    }): Observable<any> {
        const business = this.getBusiness();
        return this.create(`${this.URL}/order-detail/${business}/update-options`, payload);
    }

    // Fetch all available statuses (names/metadata)
    getOrderDetailStatuses(): Observable<{ statuses: Array<{ slug: string; name: string; description?: string; color?: string; isInitial?: boolean; isFinal?: boolean; sortOrder?: number }> }> {
        const params = this.buildParams({ businessHash: this.getBusiness() });
        return this.get<{ statuses: Array<{ slug: string; name: string; description?: string; color?: string; isInitial?: boolean; isFinal?: boolean; sortOrder?: number }> }>(`${this.URL}/order-detail/statuses`, params as any);
    }

    // Fetch item status flows from backend (optionally filtered by tableType)
    getOrderDetailFlows(tableType?: string): Observable<{ flows: Record<string, Record<string, string[]>> }> {
        const params = this.buildParams({ businessHash: this.getBusiness(), tableType });
        return this.get<{ flows: Record<string, Record<string, string[]>> }>(`${this.URL}/order-detail/flows`, params as any);
    }

    // Update flows for current business
    updateOrderDetailFlows(flows: Record<string, Record<string, { enabled: boolean; transitions: string[] }>>): Observable<{ flows: Record<string, Record<string, { enabled: boolean; transitions: string[] }>> }> {
        const businessHash = this.getBusiness();
        const url = `${this.URL}/order-detail/flows${businessHash ? `?businessHash=${businessHash}` : ''}`;
        return this.update<{ flows: Record<string, Record<string, { enabled: boolean; transitions: string[] }>> }>(url, { flows });
    }

    // Ready items endpoint
    // GET /api/v1/order-detail/ready
    // Authorization and X-Business-Uuid are attached by interceptors
    getReady(): Observable<any> {
        return this.get<any>(`${this.URL}/order-detail/ready`);
    }

    // Delete a single order detail by its UUID
    // DELETE /order-detail/by-uuid/{uuid}
    deleteOrderDetailByUuid(uuid: string): Observable<any> {
        // Endpoint does not require business segment; header is attached by ApiService
        return this.delete<any>(`${this.URL}/order-detail/by-uuid/${uuid}`);
    }
}
